import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Manages chat message area: initial scroll-to-bottom, auto-scroll on updates,
// loading older messages when reaching the top, marking messages as read,
// and intercepting form submission so sent messages appear immediately.
export default class extends Controller {
  static targets = ["messagesContainer", "messages", "topSentinel", "messageInput"]
  static values = {
    chatId: String,
    accountId: String,
    loadHistoryUrl: String,
    markReadUrl: String,
    hasUnread: { type: Boolean, default: false },
    historyCooldownMs: { type: Number, default: 1200 },
    loading: { type: Boolean, default: false }
  }

  connect() {
    console.log("ChatController connected for chat", this.chatIdValue)
    this.nextHistoryLoadAt = 0
    this.preserveScrollOnNextStream = false
    this.forceScrollToBottomOnNextStream = true
    this.markReadSent = false

    this.boundHandleBeforeStreamRender = this.handleBeforeStreamRender.bind(this)
    this.boundWindowScroll = this.onWindowScroll.bind(this)

    document.addEventListener("turbo:before-stream-render", this.boundHandleBeforeStreamRender)
    window.addEventListener("scroll", this.boundWindowScroll, { passive: true })

    if (this.hasMessagesContainerTarget) {
      this.setupTopObserver()
      this.setupReadObserver()
      this.scrollToBottom(true)
      this.startLoadingTimeout()
    }
  }

  disconnect() {
    if (this.boundHandleBeforeStreamRender) {
      document.removeEventListener("turbo:before-stream-render", this.boundHandleBeforeStreamRender)
    }
    if (this.boundWindowScroll) {
      window.removeEventListener("scroll", this.boundWindowScroll)
    }
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer)
    }
    if (this.loadingUnlockTimer) {
      clearTimeout(this.loadingUnlockTimer)
    }
    if (this.topObserver) {
      this.topObserver.disconnect()
      this.topObserver = null
    }
    if (this.readObserver) {
      this.readObserver.disconnect()
      this.readObserver = null
    }
  }

  // Scroll chat to the latest message.
  scrollToBottom(force = false) {
    if (!this.hasMessagesContainerTarget) return
    if (!force && !this.isNearBottom(120)) return

    ;[0, 60, 180, 360].forEach((delay) => {
      setTimeout(() => {
        this.scrollLastMessageIntoView()
      }, delay)
    })
  }

  scrollLastMessageIntoView() {
    if (!this.hasMessagesTarget) return

    const lastMessage = this.messagesTarget.querySelector(".message-wrapper:last-child")
    if (lastMessage) {
      lastMessage.scrollIntoView({ block: "end", inline: "nearest" })
      return
    }

    if (!this.hasMessagesContainerTarget) return

    this.messagesContainerTarget.scrollTop = this.messagesContainerTarget.scrollHeight
  }

  startLoadingTimeout() {
    const loadingIndicator = document.getElementById("history-loading-indicator")
    if (!loadingIndicator) return

    this.loadingTimer = setTimeout(() => {
      const indicator = document.getElementById("history-loading-indicator")
      if (indicator) {
        indicator.innerHTML = `
          <i class="bi bi-chat-left-text display-1 text-muted"></i>
          <p class="text-muted mt-3">Нет сообщений в этом чате</p>
          <p class="text-muted small">История недоступна или чат пуст</p>
        `
      }
    }, 30000)
  }

  handleBeforeStreamRender(event) {
    const streamElement = event.target
    if (!(streamElement instanceof Element)) return
    if (streamElement.getAttribute("target") !== "messages") return

    if (!this.hasMessagesContainerTarget) return
    const container = this.messagesContainerTarget

    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer)
      this.loadingTimer = null
    }
    if (this.loadingUnlockTimer) {
      clearTimeout(this.loadingUnlockTimer)
      this.loadingUnlockTimer = null
    }

    // Reset markReadSent so new incoming messages can trigger a new mark-read
    const action = streamElement.getAttribute("action")
    if (action === "append") {
      this.markReadSent = false
    }

    const wasNearBottom = this.isNearBottom(120)
    const shouldPreserveScroll = this.preserveScrollOnNextStream
    const previousScrollTop = container.scrollTop
    const previousScrollHeight = container.scrollHeight

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (shouldPreserveScroll) {
          const heightDelta = container.scrollHeight - previousScrollHeight
          container.scrollTop = previousScrollTop + heightDelta
          this.preserveScrollOnNextStream = false
          this.loadingValue = false
          return
        }

        if (this.forceScrollToBottomOnNextStream || wasNearBottom) {
          this.scrollLastMessageIntoView()
          this.forceScrollToBottomOnNextStream = false
        }

        // Observe any new incoming messages for read-marking
        this.observeIncomingMessages()

        // If we're near bottom and new incoming messages appeared, auto-mark as read
        if (wasNearBottom && !this.markReadSent) {
          this.sendMarkRead()
        }

        this.loadingValue = false
      })
    })
  }

  onScroll(event) {
    if (!this.hasMessagesTarget) return

    const container = event.target
    if (this.loadingValue) return
    if (Date.now() < this.nextHistoryLoadAt) return
    if (this.currentMessageCount() === 0) return

    if (container.scrollTop <= 20 && this.hasLoadHistoryUrlValue && this.loadHistoryUrlValue) {
      this.loadOlderMessages()
    }
  }

  onWindowScroll() {
    if (!this.hasTopSentinelTarget) return
    if (!this.hasMessagesTarget) return

    if (this.loadingValue) return
    if (Date.now() < this.nextHistoryLoadAt) return
    if (this.currentMessageCount() === 0) return
    if (this.isMessagesContainerScrollable()) return

    const rect = this.topSentinelTarget.getBoundingClientRect()
    if (rect.top >= 0 && rect.top <= 40) {
      this.loadOlderMessages()
    }
  }

  setupTopObserver() {
    if (!this.hasTopSentinelTarget || !this.hasMessagesContainerTarget) return

    const options = {
      root: this.isMessagesContainerScrollable() ? this.messagesContainerTarget : null,
      rootMargin: "60px 0px 0px 0px",
      threshold: 0
    }

    this.topObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        if (this.loadingValue) return
        if (Date.now() < this.nextHistoryLoadAt) return
        if (this.currentMessageCount() === 0) return

        this.loadOlderMessages()
      })
    }, options)

    this.topObserver.observe(this.topSentinelTarget)
  }

  isMessagesContainerScrollable() {
    if (!this.hasMessagesContainerTarget) return false
    const container = this.messagesContainerTarget

    return container.scrollHeight > container.clientHeight + 1
  }

  async loadOlderMessages() {
    if (this.loadingValue) return
    if (!this.hasLoadHistoryUrlValue || !this.loadHistoryUrlValue) return
    if (!this.hasMessagesTarget) return

    this.loadingValue = true
    this.nextHistoryLoadAt = Date.now() + this.historyCooldownMsValue

    try {
      const response = await fetch(this.loadHistoryUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({ loaded_count: this.currentMessageCount() })
      })

      if (response.ok) {
        const html = await response.text()
        if (html.trim().length > 0) {
          this.preserveScrollOnNextStream = true
          Turbo.renderStreamMessage(html)
        } else {
          this.scheduleLoadingUnlock()
        }
      } else {
        this.loadingValue = false
      }
    } catch (error) {
      console.error("Failed to load older messages:", error)
      this.loadingValue = false
    }
  }

  scheduleLoadingUnlock() {
    if (this.loadingUnlockTimer) {
      clearTimeout(this.loadingUnlockTimer)
    }

    this.loadingUnlockTimer = setTimeout(() => {
      this.loadingValue = false
    }, this.historyCooldownMsValue)
  }

  currentMessageCount() {
    if (!this.hasMessagesTarget) return 0

    return this.messagesTarget.querySelectorAll(".message-wrapper").length
  }

  isNearBottom(threshold = 100) {
    if (!this.hasMessagesContainerTarget) return false
    const container = this.messagesContainerTarget
    if (!this.isMessagesContainerScrollable()) return true

    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold
  }

  // --- Read-marking via IntersectionObserver ---

  // Handle message form submission: send via fetch, clear input, stay on page
  handleSendMessage(event) {
    event.preventDefault()

    const form = event.target
    const input = this.hasMessageInputTarget ? this.messageInputTarget : null
    const text = input ? input.value.trim() : ""

    if (!text) return

    // Submit via fetch to avoid page navigation
    fetch(form.action, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfToken,
        "Accept": "text/html"
      },
      body: new FormData(form)
    }).catch((err) => {
      console.error("Failed to send message:", err)
    })

    // Clear input immediately for better UX
    if (input) {
      input.value = ""
      input.focus()
    }
  }

  setupReadObserver() {
    if (!this.hasMarkReadUrlValue || !this.markReadUrlValue) return
    if (!this.hasMessagesContainerTarget) return

    const options = {
      root: this.messagesContainerTarget,
      rootMargin: "0px",
      // Multiple thresholds so callback fires as user scrolls through long messages
      threshold: [0, 0.25, 0.5, 0.75, 1.0]
    }

    this.readObserver = new IntersectionObserver((entries) => {
      if (this.markReadSent) return

      for (const entry of entries) {
        if (!entry.isIntersecting) continue

        // Mark as read only when the BOTTOM of the message is visible.
        // This matches the requirement: message becomes read when its end enters the viewport.
        if (entry.rootBounds && entry.boundingClientRect.bottom <= entry.rootBounds.bottom) {
          this.sendMarkRead()
          break
        }
      }
    }, options)

    // Observe all incoming (non-outgoing) messages
    this.observeIncomingMessages()
  }

  observeIncomingMessages() {
    if (!this.readObserver || !this.hasMessagesTarget) return

    const incomingMessages = this.messagesTarget.querySelectorAll(
      ".message-wrapper.justify-content-start"
    )
    incomingMessages.forEach((el) => this.readObserver.observe(el))
  }

  sendMarkRead() {
    if (this.markReadSent) return
    if (!this.hasMarkReadUrlValue || !this.markReadUrlValue) return

    this.markReadSent = true

    fetch(this.markReadUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        "Accept": "application/json"
      }
    }).catch((err) => {
      console.error("Failed to mark as read:", err)
      this.markReadSent = false
    })
  }

  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }
}
